import { logError } from 'fergy-core-react-logging';
import { TrackedEvent } from '../analytics/event-types';
import { type GTMEvent, type AllGTMEvents } from '../analytics/gtm/gtm-types';

export type ChatEventName =
	/* Generic chat events NOT related to a specific chat session */
	| 'afterMaximize'
	| 'afterDestroy'
	| 'afterMinimize'
	| 'onClickSubmitButton'
	| 'onHelpButtonClick'
	| 'onInviteAccepted'
	| 'onInviteRejected'
	/* All valid chat events supported by LiveAgent™
	 * Documentation: https://developer.salesforce.com/docs/atlas.en-us.snapins_web_dev.meta/snapins_web_dev/snapins_web_chat_events.htm
	 */
	| 'onAgentJoinedConference'
	| 'onAgentLeftConference'
	| 'onAgentMessage'
	| 'onAgentRichMessage'
	| 'onChasitorMessage'
	| 'onChatConferenceEnded'
	| 'onChatConferenceInitiated'
	| 'onChatEndedByAgent'
	| 'onChatEndedByChasitor'
	| 'onChatReconnectSuccessful'
	| 'onChatRequestSuccess'
	| 'onChatTransferSuccessful'
	| 'onConnectionError'
	| 'onIdleTimeoutClear'
	| 'onIdleTimeoutOccurred'
	| 'onIdleTimeoutWarningStart'
	| 'onQueueUpdate';

/**
 * Returns a forEach callback configured to track the specific event passed in.
 *
 * @param {*} embedded_svc - the LiveAgent™ object, used to register the events
 * @returns {(eventName: ChatEventName) => void} - forEach callback to track the event
 */
function registerEvent(embedded_svc: any, trackEvent: (gtmType: string) => void): (eventName: ChatEventName) => void {
	return (eventName: ChatEventName) => {
		embedded_svc.addEventHandler(eventName, () => trackEvent(eventName));
	};
}

/**
 * Registers each event to send tracking to GTM
 *
 * @param {*} embedded_svc - the LiveAgent™ object, used to register the events
 * @param {ChatEventName[]} events - an array of event names to track see above for the complete list
 */
export function registerTrackingEvents(embedded_svc: any, events: ChatEventName[], trackEvent: (gtmType: string) => void) {
	events.forEach(registerEvent(embedded_svc, trackEvent));
}

/**
 * Opens the live agent chat UI
 */
export const initiateLiveChat = (trackLiveChat: (gtmType: string) => void) => {
	if (window.embedded_svc?.inviteAPI) {
		try {
			window.embedded_svc.inviteAPI.inviteButton.acceptInvite();
		} catch {
			// swallow error
		}
	}

	// track event
	trackLiveChat('chat-click');
};

/**
 * Tracks a live chat initiation event
 */
export const makeTrackLiveChat = (trackEvent: (event: GTMEvent<AllGTMEvents>) => void): ((gtmType: string) => void) => {
	return (gtmType: string) => {
		const { page, product } = window.dataLayer;
		trackEvent({
			event: TrackedEvent.LIVE_CHAT_INTERACTION,
			interaction: gtmType,
			pageName: page,
			baseCategory: product?.baseCategory,
			businessCategory: product?.businessCategory
		});
	};
};

/**
 * Opens the Enhanced Einstein Bot UI
 */
export const initiateEnhancedChat = (trackEnhancedChat: (gtmType: string) => void) => {
	if (window.embeddedservice_bootstrap?.utilAPI) {
		try {
			window.embeddedservice_bootstrap.utilAPI.launchChat();
		} catch (error) {
			logError(error);
		}
	}

	// track event
	trackEnhancedChat('enh-chat-click');
};
